import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '../metas.css';
import { RoleService } from '../../layouts/services/role.service';
import { MetasColaboradorService } from '../services/metasColaborador.service';
import type { ContadoresPestanas, MiAlcance, TiendaRef } from '../types/metas.types';
import { almacenSesion, avisarAlcanceMetas, guardarMemoMenu } from '../utils/menu';
import { GRUPO_CONFIRMAR } from '../utils/confirmar';
import { AccesosPanel } from '../components/AccesosPanel';
import { etiquetaAlcance } from '../components/AccesosAvisos';
import { AlcancesTab } from '../components/AlcancesTab';
import { AutorizacionTab } from '../components/AutorizacionTab';
import { CapturaTab } from '../components/CapturaTab';
import { PendientesTab } from '../components/PendientesTab';
import { BarraPestanas, PestanaNav } from '../components/ui/BarraPestanas';
import { SelectorSemana } from '../components/ui/SelectorSemana';
import { CLASE_OVERLAY } from '../components/ui/clases';
import { limiteRegional, semanasDelSelector } from '../utils/semanas';
import { soloSeguimiento } from '../utils/direccion';
import { capturaDeTienda } from '../utils/flujo';
import { esSemanaCerrada, semanasCerradas, ultimaSemanaCerrada } from '../utils/alcances';
import {
  leerSemanaParam,
  leerTiendaParam,
  Pestana,
  pestanaDeRuta,
  rutaPestana,
  TiendaParam,
  tiendaParam,
} from '../utils/rutas';

export const SIN_ALCANCE =
  'Tu cuenta no tiene tiendas asignadas para metas. Pide a un administrador que te asigne como supervisor o regional.';

export const TITULO = 'Metas por colaborador';
/**
 * El flujo de dos pasos: el supervisor captura y el regional autoriza. Es el
 * que corre mientras la captura de la tienda esté apagada (22-oct-2026,
 * decisión del usuario: «de momento seguir dejando la tarea de capturar la
 * meta a los supervisores»), que es lo que hay por omisión.
 */
export const SUBTITULO =
  'Meta semanal de venta por colaborador (lunes a domingo, sin IVA). El supervisor captura y envía; el regional autoriza o rechaza la tienda completa. Lo autorizado alimenta el cálculo de comisiones.';
/**
 * 21-oct-2026 (decisión del usuario, fase B): el gerente captura desde la app de
 * la tienda, el supervisor revisa y el regional autoriza. Sólo con `capturaTienda`.
 */
export const SUBTITULO_CAPTURA_TIENDA =
  'Meta semanal de venta por colaborador (lunes a domingo, sin IVA). El gerente la captura desde la app de la tienda; el supervisor la revisa, la ajusta si hace falta y la envía; el regional autoriza o rechaza la tienda completa. Lo autorizado alimenta el cálculo de comisiones.';
/** Dirección (21-oct-2026): lo mismo dicho para quien sigue y no decide (flujo de dos pasos, 22-oct-2026). */
export const SUBTITULO_DIRECCION =
  'Seguimiento de la meta semanal de venta por colaborador en todas las tiendas (lunes a domingo, sin IVA): con quién está cada tienda, quién capturó, envió y autorizó, la venta contra la meta y lo que falta en el catálogo. Sólo lectura: el supervisor captura y envía, y el regional autoriza.';
/** Dirección con la captura de la tienda encendida (fase B). */
export const SUBTITULO_DIRECCION_CAPTURA_TIENDA =
  'Seguimiento de la meta semanal de venta por colaborador en todas las tiendas (lunes a domingo, sin IVA): con quién está cada tienda, quién capturó, envió y autorizó, la venta contra la meta y lo que falta en el catálogo. Sólo lectura: el gerente captura, el supervisor revisa y el regional autoriza.';

interface DefPestana {
  id: Pestana;
  titulo: string;
  tituloCorto?: string;
}

/**
 * Metas por colaborador.
 *
 * POR QUÉ EXISTE
 * Las metas semanales llegaban en un Excel por regional, tarde y sucias: 58 %
 * de cobertura en la semana 38, texto en la casilla de la meta, metas sin un
 * cero, renglones duplicados. Sin el dato no se pueden pagar las comisiones.
 * Aquí el supervisor captura la meta de su gente y la envía, y el regional
 * autoriza la tienda completa; lo autorizado alimenta el cálculo. Con la
 * captura de la tienda encendida (`capturaTienda` de `mi-alcance`, por
 * entorno) la captura el gerente desde la app de la tienda (21-oct-2026) y el
 * supervisor la revisa; de momento está APAGADA (22-oct-2026, decisión del
 * usuario: «seguir dejando la tarea de capturar la meta a los supervisores»).
 *
 * QUIÉN VE QUÉ
 * Lo decide `mi-alcance` (la API), no el rol: los supervisores y regionales
 * son cuentas Indicadores con un alcance asignado. Las pestañas salen de ahí:
 * Captura (supervisor), Autorización (regional) y Accesos (administrador).
 * La pantalla se protege sola: sin alcance sólo dice que no hay tiendas.
 * Dirección (21-oct-2026, decisión del usuario: «un rol más directivo con
 * información importante») ve TODAS las tiendas en sólo lectura: Autorización
 * como «Seguimiento» (sin decisiones), Pendientes y Alcances con el filtro de
 * regional. Nada de Captura, Accesos ni «Bajar autorizadas».
 *
 * FASE 4: «Pendientes de catálogo» vuelve como pestaña, ahora de CALIDAD DE
 * DATOS (lo que falta en BUK, el ERP o la fuente de metas de tienda y bloquea
 * la captura o las comisiones), para supervisor, regional y admin. Las
 * propuestas de suplentes siguen en Autorización («Suplentes por autorizar»;
 * las condiciones de tienda ya no se proponen desde el 21/22-oct-2026, pero las
 * que quedaron propuestas salen ahí, en «Condiciones por resolver»). Los
 * contadores salen de `GET …/contadores`: Captura (lo que el gerente le mandó y
 * espera su revisión, 21-oct-2026), Autorización (enviadas que ESTA cuenta puede
 * autorizar MÁS las propuestas que puede resolver, 22-oct-2026), en azul, y
 * Pendientes (abiertos que la cuenta ve, en ámbar).
 *
 * FASE 5: «Alcances» (venta real contra meta) para supervisor, regional y
 * admin. Mide semanas CERRADAS, así que lleva su PROPIA semana: mientras se ve,
 * el selector de la cabecera ofrece sólo semanas cerradas y el enlace lleva
 * ésa; la Captura y Autorización conservan la suya (actual o siguiente).
 *
 * RUTAS (fase 2a del rediseño)
 * `App.tsx` monta esta pantalla UNA vez en `/metas/*`; la pestaña sale de la
 * ruta (`/metas/captura|autorizacion|pendientes|alcances|accesos`) y la semana y la
 * tienda de `?semana=AAAA-MM-DD&tienda=VAZ-1`, que se leen al entrar y se
 * escriben al cambiar: el enlace se puede compartir. `/metas` a secas (los
 * enlaces viejos) lleva a la primera pestaña permitida.
 *
 * ⚠️ Las pestañas inactivas quedan MONTADAS y ocultas (`hidden`): la app usa
 * `BrowserRouter` (no hay `useBlocker`), así que desmontar la Captura al ir a
 * Autorización perdería lo escrito sin guardar sin poder avisar.
 */
export const MetasColaboradorScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Lo que traía el enlace AL ENTRAR. Después la pantalla manda y lo escribe.
  const inicio = useRef<{ semana: string | null; tienda: TiendaParam | null; pestana: string | null } | null>(null);
  if (inicio.current === null) {
    const q = new URLSearchParams(location.search);
    inicio.current = {
      semana: leerSemanaParam(q.get('semana')),
      tienda: leerTiendaParam(q.get('tienda')),
      pestana: pestanaDeRuta(location.pathname),
    };
  }

  const toast = useRef<Toast>(null);
  const [alcance, setAlcance] = useState<MiAlcance | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lunes, setLunes] = useState<string | null>(null);
  // La tienda de cada pestaña, por separado: la Captura y Autorización abren
  // tiendas distintas, y el enlace lleva la de la pestaña que se ve.
  const tiendaInicialCaptura = inicio.current.pestana === 'autorizacion' ? null : inicio.current.tienda;
  const [tienda, setTienda] = useState<TiendaParam | null>(tiendaInicialCaptura);
  // La del enlace va a Autorización sólo si el enlace era de Autorización.
  const tiendaInicialAut = inicio.current.pestana === 'autorizacion' ? inicio.current.tienda : null;
  const [tiendaAut, setTiendaAut] = useState<TiendaParam | null>(tiendaInicialAut);
  // Alcances mide semanas CERRADAS: su semana y su tienda van aparte de las de captura.
  const deAlcances = inicio.current.pestana === 'alcances';
  const tiendaInicialAlc = deAlcances ? inicio.current.tienda : null;
  const [tiendaAlc, setTiendaAlc] = useState<TiendaParam | null>(tiendaInicialAlc);
  const [semanaAlc, setSemanaAlc] = useState<string | null>(null);
  const capturaSucia = useRef(false);
  // «Guarda lo pendiente ya»: la Captura lo deja aquí y se llama antes de
  // cambiar de semana (el autoguardado espera 2 s; cambiar no espera).
  const guardarCaptura = useRef<(() => Promise<boolean>) | null>(null);
  // Mientras la captura guarda o envía, la semana no se cambia: la respuesta
  // llegaría con otra semana en pantalla.
  const [capturaOcupada, setCapturaOcupada] = useState(false);
  // Los números de las pestañas (`GET …/contadores`), y un contador para que
  // Autorización recargue su lista y sus propuestas cuando algo del catálogo
  // cambió desde la Captura.
  const [contadores, setContadores] = useState<ContadoresPestanas | null>(null);
  const [recargaAutorizacion, setRecargaAutorizacion] = useState(0);
  // Y al revés: Autorización resolvió algo y la Captura (montada y oculta) relee.
  const [recargaCaptura, setRecargaCaptura] = useState(0);
  // «Ver captura completa» desde Autorización: la tienda que la Captura debe
  // abrir. `n` cambia en cada pedido (la misma tienda dos veces también cuenta).
  const [abrirEnCaptura, setAbrirEnCaptura] = useState<(TiendaRef & { n: number }) | null>(null);
  // «Ver tienda» desde Pendientes para quien no captura: Autorización la abre.
  const [abrirEnAutorizacion, setAbrirEnAutorizacion] = useState<(TiendaRef & { n: number }) | null>(null);
  // «Ver en Pendientes de catálogo» desde el banner: la lista abre filtrada a esa tienda.
  const [pendientesDeTienda, setPendientesDeTienda] = useState<(TiendaRef & { n: number }) | null>(null);

  const avisar = useCallback(
    (severity: 'success' | 'warn' | 'error', summary: string, detail?: string) =>
      toast.current?.show({ severity, summary, detail, life: severity === 'success' ? 4000 : 9000 }),
    [],
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const a = await MetasColaboradorService.getMiAlcance();
      setAlcance(a);
      const semanas = semanasDelSelector(a);
      // La semana del enlace vale si la cuenta la puede tocar (el admin, cualquiera).
      const pedida = inicio.current?.semana ?? null;
      // La semana de un enlace de Alcances es una CERRADA: es de esa pestaña, no de la captura.
      const enlaceDeAlcances = inicio.current?.pestana === 'alcances';
      const delEnlace =
        pedida && !enlaceDeAlcances && (a.esAdmin || semanas.includes(pedida)) ? pedida : null;
      setSemanaAlc(
        (s) => s ?? (enlaceDeAlcances && esSemanaCerrada(pedida, a.hoy) ? pedida : ultimaSemanaCerrada(a.hoy)),
      );
      setLunes(
        (l) => l ?? delEnlace ?? (semanas.includes(a.semanaActual) ? a.semanaActual : semanas[0] ?? a.semanaActual),
      );
      // El menú se decide con esta misma respuesta: si el alcance cambió, que
      // el menú lo sepa sin esperar a que caduque su memoria, y que el item
      // aparezca o desaparezca YA (el layout escucha el aviso).
      const email = RoleService.getUserName() ?? '';
      guardarMemoMenu(almacenSesion(), email, a.tipo, Date.now());
      avisarAlcanceMetas(email, a.tipo);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const pestanas = useMemo(() => {
    const p: DefPestana[] = [];
    if (!alcance) return p;
    // Para el supervisor es «Revisión» (diseño `Supervisor-revision`, 21-oct-2026): el
    // gerente captura y él revisa. La ruta sigue siendo `captura`; el admin la ve igual.
    // Sólo con la captura de la tienda encendida: apagada (22-oct-2026, decisión del
    // usuario) él captura, y la pestaña vuelve a ser «Captura».
    if (alcance.puedeCapturar)
      p.push({
        id: 'captura',
        titulo: capturaDeTienda(alcance) && alcance.tipo === 'supervisor' && !alcance.esAdmin ? 'Revisión' : 'Captura',
      });
    if (alcance.puedeAutorizar) p.push({ id: 'autorizacion', titulo: 'Autorización' });
    // Dirección (21-oct-2026): la misma pestaña (y la misma ruta), en sólo lectura.
    else if (soloSeguimiento(alcance)) p.push({ id: 'autorizacion', titulo: 'Seguimiento' });
    // Calidad de datos (fase 4): todo el que tiene alcance (supervisor, regional, admin).
    if (alcance.tipo !== null) p.push({ id: 'pendientes', titulo: 'Pendientes de catálogo', tituloCorto: 'Pendientes' });
    // Venta real contra meta (fase 5): supervisor, regional y admin, cada quien sus tiendas.
    if (alcance.tipo !== null) p.push({ id: 'alcances', titulo: 'Alcances' });
    if (alcance.esAdmin) p.push({ id: 'accesos', titulo: 'Accesos' });
    return p;
  }, [alcance]);

  // La pestaña de la ruta, si la cuenta la puede ver; si no, la primera.
  const pedidaEnRuta = pestanaDeRuta(location.pathname);
  const activa: Pestana | null = pestanas.some((p) => p.id === pedidaEnRuta)
    ? (pedidaEnRuta as Pestana)
    : pestanas[0]?.id ?? null;

  // Una navegación de la propia pantalla («Ver captura completa») que todavía no
  // llega a `location`: react-router 7 navega dentro de una transición, así que
  // el render que causa `setTienda` ve la ruta VIEJA, y el efecto de abajo
  // reescribía la ruta de vuelta a Autorización pisando la navegación.
  const navegandoA = useRef<string | null>(null);

  /**
   * La búsqueda (`?semana&tienda`) de una pestaña: la semana en pantalla y la
   * tienda ABIERTA en esa pestaña. Si la pestaña avisa que no quedó ninguna, se
   * borra: un `&tienda=` que no se ve abría otra cosa a quien recibía el enlace
   * (Autorización conservaba la de la Captura y mostraba otra tienda).
   */
  const busquedaDe = useCallback(
    (p: Pestana): string => {
      const q = new URLSearchParams(location.search);
      const s = p === 'alcances' ? semanaAlc : lunes;
      if (s) q.set('semana', s);
      const t = p === 'captura' ? tienda : p === 'autorizacion' ? tiendaAut : p === 'alcances' ? tiendaAlc : null;
      if (t) q.set('tienda', tiendaParam(t));
      else q.delete('tienda');
      return q.toString() ? `?${q.toString()}` : '';
    },
    [location.search, lunes, tienda, tiendaAut, semanaAlc, tiendaAlc],
  );

  // La URL sigue a la pantalla: pestaña, semana y tienda. `replace`: elegir una
  // semana no deja un paso de «atrás» por cada clic.
  useEffect(() => {
    if (!activa) return;
    if (navegandoA.current) {
      if (location.pathname !== navegandoA.current) return;
      navegandoA.current = null;
    }
    const pathname = rutaPestana(activa);
    const search = busquedaDe(activa);
    if (pathname !== location.pathname || search !== location.search) {
      navigate({ pathname, search }, { replace: true });
    }
  }, [activa, busquedaDe, location.pathname, location.search, navigate]);

  const alCambiarCaptura = useCallback((sucio: boolean) => {
    capturaSucia.current = sucio;
  }, []);

  const alAbrirTienda = useCallback((t: TiendaParam | null) => {
    setTienda((actual) => (actual && t && actual.chain === t.chain && actual.num === t.num ? actual : t));
  }, []);

  const alAbrirTiendaAut = useCallback((t: TiendaParam | null) => {
    setTiendaAut((actual) => (actual && t && actual.chain === t.chain && actual.num === t.num ? actual : t));
  }, []);

  const alAbrirTiendaAlc = useCallback((t: TiendaParam | null) => {
    setTiendaAlc((actual) => (actual && t && actual.chain === t.chain && actual.num === t.num ? actual : t));
  }, []);

  // Lo propuesto desde la Captura tiene que aparecer en «N de catálogo por
  // resolver» y en la sección de propuestas de Autorización.
  const alCambiarCatalogoEnCaptura = useCallback(() => setRecargaAutorizacion((n) => n + 1), []);

  // Contadores de las pestañas: sólo Postgres, baratos. Sólo cuenta la respuesta
  // del último pedido (al cambiar de semana, la anterior llegaba tarde).
  // Los pide todo el que tiene alcance: el de Pendientes también es del supervisor.
  const semanaContadores = lunes ?? alcance?.semanaActual ?? null;
  const puedeAutorizar = !!alcance?.puedeAutorizar;
  // Dirección sin regional: la pestaña Autorización es «Seguimiento», sin decisiones.
  const seguimiento = !!alcance && soloSeguimiento(alcance);
  const conAlcance = !!alcance && alcance.tipo !== null;
  const pedidoContadores = useRef(0);
  const refrescarContadores = useCallback(async () => {
    const n = ++pedidoContadores.current;
    if (!conAlcance || !semanaContadores) {
      setContadores(null);
      return;
    }
    try {
      const c = await MetasColaboradorService.contadores(semanaContadores);
      if (n === pedidoContadores.current) setContadores(c);
    } catch {
      // Un contador que no se pudo leer no se inventa: la pestaña queda sin número.
      if (n === pedidoContadores.current) setContadores(null);
    }
  }, [conAlcance, semanaContadores]);
  useEffect(() => {
    void refrescarContadores();
  }, [refrescarContadores]);

  // Las pestañas quedan montadas: lo que cambia el ESTADO de una tienda en una
  // tiene que llegar a la otra. Desde la Captura (enviar, reabrir): la lista y
  // el contador de Autorización. Desde Autorización (autorizar, rechazar,
  // reabrir, el lote): el contador y la Captura.
  const alCambiarEstadoEnCaptura = useCallback(() => {
    setRecargaAutorizacion((n) => n + 1);
    void refrescarContadores();
  }, [refrescarContadores]);
  const alCambiarEnAutorizacion = useCallback(() => {
    setRecargaCaptura((n) => n + 1);
    void refrescarContadores();
  }, [refrescarContadores]);

  const puedeCapturar = !!alcance?.puedeCapturar;
  const hrefCaptura = useCallback(
    (t: TiendaRef): string | null => {
      if (!puedeCapturar) return null;
      const q = new URLSearchParams(location.search);
      if (lunes) q.set('semana', lunes);
      q.set('tienda', tiendaParam(t));
      return `${rutaPestana('captura')}?${q.toString()}`;
    },
    [puedeCapturar, location.search, lunes],
  );

  // La tienda NO se escribe aquí: la Captura la informa (`onTienda`) cuando de
  // verdad la abre. Si tenía cambios que no se pudieron guardar y se elige
  // «Seguir aquí», la URL decía otra tienda que la de la pantalla.
  const irACaptura = useCallback(
    (t: TiendaRef) => {
      navegandoA.current = rutaPestana('captura');
      setAbrirEnCaptura((a) => ({ chain: t.chain, num: t.num, n: (a?.n ?? 0) + 1 }));
      navigate({ pathname: rutaPestana('captura'), search: busquedaDe('captura') });
    },
    [navigate, busquedaDe],
  );

  /** «Ver en Pendientes de catálogo» desde el banner de la Captura: filtrada a ESA tienda (F4-V7). */
  const irAPendientes = useCallback((t?: TiendaRef) => {
    if (t) setPendientesDeTienda((a) => ({ chain: t.chain, num: t.num, n: (a?.n ?? 0) + 1 }));
    navegandoA.current = rutaPestana('pendientes');
    navigate({ pathname: rutaPestana('pendientes'), search: busquedaDe('pendientes') });
  }, [navigate, busquedaDe]);

  /**
   * «Ver tienda» desde Pendientes: la Captura si la cuenta captura; si no,
   * Autorización (o «Seguimiento», para dirección: antes no hacía nada). Con la
   * semana del pendiente (tienda sin meta, en dos tiendas) si la cuenta la puede
   * ver; si no, la de pantalla.
   */
  const verTiendaDePendiente = (t: TiendaRef, semana: string | null) => {
    if (!alcance) return;
    const otra =
      semana && semana !== lunes && (alcance.esAdmin || semanasDelSelector(alcance).includes(semana)) ? semana : null;
    if (otra) void cambiarSemana(otra);
    if (puedeCapturar) {
      irACaptura(t);
      return;
    }
    if (puedeAutorizar || seguimiento) {
      navegandoA.current = rutaPestana('autorizacion');
      setAbrirEnAutorizacion((a) => ({ chain: t.chain, num: t.num, n: (a?.n ?? 0) + 1 }));
      navigate({ pathname: rutaPestana('autorizacion'), search: busquedaDe('autorizacion') });
    }
  };

  /**
   * Cambiar de semana guarda antes lo que la Captura tenga pendiente. Sólo si
   * no se puede (errores de celda, sólo lectura, un 409) pregunta si se descarta.
   */
  const cambiarSemana = async (nuevo: string) => {
    if (nuevo === lunes) return;
    if (!capturaSucia.current) {
      setLunes(nuevo);
      return;
    }
    const guardado = guardarCaptura.current ? await guardarCaptura.current() : false;
    if (guardado) {
      setLunes(nuevo);
      return;
    }
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Cambios sin guardar',
      message: 'No se pudo guardar lo que escribiste en la captura. Si cambias de semana se pierde. ¿Continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, descartar',
      rejectLabel: 'Seguir aquí',
      accept: () => setLunes(nuevo),
    });
  };

  // En el teléfono (diseño `MovilCaptura`) no van ni el título grande —ya está
  // en la barra superior del panel; aquí queda para el lector— ni la
  // descripción: empujaban la primera tarjeta muy por debajo del pliegue.
  // Dirección que sólo sigue (sin captura ni autorización): la descripción dice lo que ve.
  const soloDireccion = seguimiento && !alcance?.puedeCapturar;
  // Quién captura lo dice el entorno (22-oct-2026): por omisión, el supervisor.
  const capturaTienda = capturaDeTienda(alcance);
  const encabezadoTexto = (
    <div className="flex max-w-[700px] flex-col gap-1.5">
      <h1 className="m-0 text-metas-h1 text-metas-texto max-md:sr-only">{TITULO}</h1>
      <p className="m-0 text-sm leading-normal text-metas-texto-2 max-md:hidden" data-testid="metas-descripcion">
        {soloDireccion
          ? capturaTienda
            ? SUBTITULO_DIRECCION_CAPTURA_TIENDA
            : SUBTITULO_DIRECCION
          : capturaTienda
            ? SUBTITULO_CAPTURA_TIENDA
            : SUBTITULO}
      </p>
    </div>
  );

  const raiz = 'metas-ui min-h-full bg-metas-fondo px-4 pb-6 pt-4 font-metas text-metas-texto md:px-10 md:pt-7';

  if (cargando && !alcance) {
    return (
      <div className={raiz}>
        {encabezadoTexto}
        <div className="mt-4 text-metas-texto-2">Cargando tu alcance…</div>
      </div>
    );
  }

  if (error && !alcance) {
    return (
      <div className={raiz}>
        {encabezadoTexto}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Message severity="error" text={`No se pudo leer tu alcance: ${error}`} />
          <Button label="Reintentar" icon="pi pi-refresh" size="small" onClick={() => void cargar()} />
        </div>
      </div>
    );
  }

  if (!alcance || alcance.tipo === null || !pestanas.length || !activa) {
    return (
      <div className={raiz}>
        {encabezadoTexto}
        <div className="mt-4" data-testid="sin-alcance">
          <Message severity="info" text={SIN_ALCANCE} />
        </div>
      </div>
    );
  }

  const semana = lunes ?? alcance.semanaActual;
  const semanaAlcances = semanaAlc ?? ultimaSemanaCerrada(alcance.hoy);

  const nav: PestanaNav[] = pestanas.map((p) => ({
    id: p.id,
    titulo: p.titulo,
    tituloCorto: p.tituloCorto,
    to: { pathname: rutaPestana(p.id), search: busquedaDe(p.id) },
    contador:
      // Lo que el gerente le mandó: sólo existe con la captura de la tienda (22-oct-2026).
      p.id === 'captura' && capturaTienda && contadores?.porRevisar
        ? contadores.porRevisar
        : // Tiendas por autorizar y propuestas por resolver (condiciones que quedaron y
          // suplentes): las dos esperan una decisión de esta cuenta (22-oct-2026).
          p.id === 'autorizacion' && (contadores?.porAutorizar || contadores?.propuestas)
          ? (contadores.porAutorizar ?? 0) + (contadores.propuestas ?? 0)
          : p.id === 'pendientes' && contadores?.pendientesCatalogo
            ? contadores.pendientesCatalogo
            : null,
    // Lo que espera una decisión, en azul; lo que bloquea (catálogo), en ámbar.
    tonoContador: p.id === 'pendientes' ? 'aviso' : 'info',
  }));

  return (
    <div className={raiz}>
      <Toast ref={toast} className={CLASE_OVERLAY} />
      <ConfirmDialog group={GRUPO_CONFIRMAR} className={CLASE_OVERLAY} />

      {/* El administrador tiene además «Otra semana» (un renglón más a la derecha):
          alineada abajo, su título bajaba ~85 px y quedaba desalineado. */}
      <header
        // En fila sólo desde lg: a 768 el título y la descripción quedaban en una
        // columna de ~140 px y empujaban el contenido ~1.000 px (auditoría del 22-oct-2026).
        className={`flex flex-col gap-4 max-md:gap-3 lg:flex-row lg:justify-between lg:gap-8 ${
          alcance.esAdmin && activa !== 'alcances' ? 'lg:items-start' : 'lg:items-end'
        }`}
        data-testid="metas-cabecera"
      >
        {encabezadoTexto}
        {activa === 'alcances' ? (
          // Sólo semanas CERRADAS (y sin «Otra semana»: el calendario dejaba elegir una abierta).
          <SelectorSemana
            semanas={semanasCerradas(alcance.hoy)}
            semanaActual={alcance.semanaActual}
            value={semanaAlcances}
            onChange={(l) => setSemanaAlc(l)}
          />
        ) : (
          <SelectorSemana
            semanas={semanasDelSelector(alcance)}
            semanaActual={alcance.semanaActual}
            value={semana}
            onChange={(l) => void cambiarSemana(l)}
            permitirOtra={alcance.esAdmin}
            disabled={capturaOcupada}
          />
        )}
      </header>

      {!alcance.esAdmin && alcance.alcances.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 text-sm text-metas-texto-2 max-md:hidden" data-testid="metas-tu-alcance">
          <span>Tu alcance:</span>
          {alcance.alcances.map((a) => (
            <strong key={`${a.tipo}|${a.nombre}`} className="font-semibold text-metas-texto">
              {etiquetaAlcance(a)}
            </strong>
          ))}
          <span>· {alcance.tiendas.length} tienda(s)</span>
          {/* Diseño `Regional-filtro` (revisión de la fase B, 21-oct-2026): el viernes en
              que autoriza, mientras no pase. Sólo recuerda; nada se bloquea por día. */}
          {activa === 'autorizacion' && alcance.puedeAutorizar && alcance.hoy <= limiteRegional(semana).fecha && (
            <span data-testid="metas-limite-regional">· autoriza a más tardar el {limiteRegional(semana).texto}</span>
          )}
        </div>
      )}

      <BarraPestanas pestanas={nav} className="mt-5 max-md:mt-3" />

      <div className="pt-6 max-md:pt-4">
        {pestanas.map((p) => (
          <div key={p.id} data-panel={p.id} hidden={p.id !== activa}>
            {p.id === 'captura' && (
              <CapturaTab
                alcance={alcance}
                lunes={semana}
                onAviso={avisar}
                onCambiosSinGuardar={alCambiarCaptura}
                onOcupado={setCapturaOcupada}
                onCambioCatalogo={alCambiarCatalogoEnCaptura}
                tiendaInicial={tiendaInicialCaptura}
                onTienda={alAbrirTienda}
                guardarPendienteRef={guardarCaptura}
                abrirTienda={abrirEnCaptura}
                recarga={recargaCaptura}
                onCambioEstado={alCambiarEstadoEnCaptura}
                onVerPendientes={irAPendientes}
              />
            )}
            {p.id === 'autorizacion' && (
              <AutorizacionTab
                alcance={alcance}
                lunes={semana}
                onAviso={avisar}
                recarga={recargaAutorizacion}
                activa={activa === 'autorizacion'}
                onCambio={alCambiarEnAutorizacion}
                hrefCaptura={hrefCaptura}
                onIrACaptura={irACaptura}
                tiendaInicial={tiendaInicialAut}
                onTienda={alAbrirTiendaAut}
                abrirTienda={abrirEnAutorizacion}
                soloLectura={seguimiento}
              />
            )}
            {p.id === 'pendientes' && (
              <PendientesTab
                alcance={alcance}
                onAviso={avisar}
                activa={activa === 'pendientes'}
                onVerTienda={verTiendaDePendiente}
                onCambio={refrescarContadores}
                filtroTienda={pendientesDeTienda}
              />
            )}
            {p.id === 'alcances' && (
              <AlcancesTab
                alcance={alcance}
                lunes={semanaAlcances}
                onAviso={avisar}
                activa={activa === 'alcances'}
                tiendaInicial={tiendaInicialAlc}
                onTienda={alAbrirTiendaAlc}
              />
            )}
            {p.id === 'accesos' && <AccesosPanel onAviso={avisar} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetasColaboradorScreen;
